'use client';

import { Container } from '@/components/container';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';

interface FAQItem {
  question: string;
  answer: string;
}

export default function Faqs() {
  const faqItems: Record<string, FAQItem[]> = {
    general: [
      {
        question: 'What is InnovationHUBSolutions (IHQX)?',
        answer:
          'InnovationHUBSolutions is a collaborative B2B platform designed to help workers across industries, company size, functional areas, and job roles to navigate the detailed aspects of workplace innovation and change management by sharing real-world experiences, actionable insights, and peer-driven solutions.',
      },
      {
        question: 'Who is this platform for?',
        answer:
          'IHQX is designed for a broad spectrum of workers across the business ecosystem—from solo practitioners and small business owners to employees at large corporations and professionals in nonprofit organizations. Whether frontline staff, managers, or executives, users can engage with real-world workplace challenges, share their experiences, and collaborate on practical, innovative solutions.',
      },
      {
        question: 'How does IHQX work?',
        answer:
          'Users can ask questions, share stories, and engage in shared discussions around innovation and change management. The platform curates high-quality, peer-driven content to foster learning and collaboration.',
      },
      {
        question: 'How is IHQX different from other business Q&A platforms?',
        answer:
          'Unlike generic Q&A platforms, IHQX focuses exclusively on innovation and change management to offer structured, experience-based content that is deeply relevant to business professionals.',
      },
    ],

    usingThePlatform: [
      {
        question: 'How do I submit a story?',
        answer:
          'You can add your story by filling out the "Add Your Story" form, which includes fields for headlines, theme, narrative, and optional key takeaways.',
      },
      {
        question: 'What types of stories can I share?',
        answer:
          'Stories should focus on real-world experiences in workplace innovation or change management. They can be categorized as Experience, Historical, Research, or Opinion-based insights.',
      },
      {
        question: 'Can I edit my story after submission?',
        answer:
          'Yes, users can edit their stories to refine or update information.',
      },
      {
        question: 'Is there a limit on the length of stories?',
        answer:
          'Yes, to ensure readability and engagement, stories should be less than 1500 characters. This encourages concise storytelling and allows users to create linked, related stories if needed.',
      },
      {
        question: 'Can I ask a question instead of sharing a story?',
        answer:
          'Absolutely! The "Ask a Question" form allows users to submit direct workplace-related questions, with a 150–200-character limit for clarity, and an optional background section of 1,000–1,500 characters for additional context.',
      },
    ],
    engagementAndFeatures: [
      {
        question: 'Can I interact with other users’ stories and questions',
        answer:
          'Yes! You can engage by commenting, and contributing insights based on your own experiences.',
      },
      {
        question: 'Will my contributions be public?',
        answer: `Yes, but you control how much detail you share. Contributors who add a story have the option to publish their real-life experiences anonymously, which ensures privacy while still contributing valuable insights. However, sharing workplace experiences may conflict with your company's policies, so we encourage users to review their organization’s guidelines before posting. Certain user profile features may also be available for private networking in later development phases.`,
      },
      {
        question: 'Is there a way to connect my stories into a series?',
        answer:
          'Yes, IHQX supports linked storytelling which allows users to create a series of related stories to build on previous experiences.',
      },
      {
        question: 'Will the platform offer image uploads?',
        answer: 'Yes, users can upload images while they are posting a story.',
      },
      {
        question: 'Are there guidelines for posting content?',
        answer: `All content shared on IHQX should be based on real-life workplace experiences to ensure authenticity and practical value for users. Contributions should be respectful, courteous, and relevant to workplace innovation and change management to foster meaningful discussions that help others navigate similar challenges. To maintain the platform’s quality and credibility, all submissions are subject to moderation. This includes reviewing content for accuracy, appropriateness, and alignment with IHQX’s mission of providing actionable insights. Posts that contain misleading information, promotional content, or violate community guidelines may be flagged for review or removed to uphold the integrity of the platform.`,
      },
    ],
    userAccountsAndPrivacy: [
      {
        question: 'Do I need to create an account to use IHQX?',
        answer: `Basic browsing is open to all, but to post a question or story, an account with a verified email is required.`,
      },
      {
        question: 'Will my personal data be protected?',
        answer: `Yes, IHQX follows strict data privacy and security measures to protect user information.  This is our commitment to you.`,
      },
      {
        question: 'Can I delete my account or remove a post?',
        answer: `Yes, users can request the removal of posts or account deletion through the platform’s account settings.`,
      },
    ],
    futurePlansAndDevelopments: [
      {
        question: 'What features are planned for future updates?',
        answer: `Upcoming features may include curated industry-specific content, expert panels, deeper analytics, and expanded community engagement tools.`,
      },
      {
        question: 'How can I provide feedback on the platform?',
        answer: `User feedback is critical to improving IHQX! You can share suggestions through our feedback form on the Homepage or participate in user experience discussions.`,
      },
      {
        question: 'How can my company or organization collaborate with IHQX?',
        answer: `For partnerships, corporate engagement, or content licensing inquiries, please contact us at hello@innovationhubsolutions.com.`,
      },
    ],
  };

  const tabLabels: { key: keyof typeof faqItems; label: string }[] = [
    { key: 'general', label: 'General Questions' },
    { key: 'usingThePlatform', label: 'Using the Platform' },
    { key: 'engagementAndFeatures', label: 'Engagement & Features' },
    { key: 'userAccountsAndPrivacy', label: 'User Accounts & Privacy' },
    { key: 'futurePlansAndDevelopments', label: 'Future Plans & Development' },
  ];

  return (
    <section className="p-8 w-full mx-auto">
      <Container>
        <div className="py-8 flex flex-col gap-8">
          {/* Header Section */}
          <div className="flex flex-col text-center gap-4">
            <div className="font-bold text-3xl tracking-tight">
              Frequently Asked Questions
            </div>
            <h4 className="font-bold text-xl tracking-tight">
              Have questions? We're here to help.
            </h4>
          </div>

          {/* Tabs Layout */}
          <Tabs
            variant="underline"
            defaultValue={tabLabels[0]?.key ?? ''}
            className="w-full"
          >
            <TabsList className="mb-8 flex flex-wrap justify-center gap-2">
              {tabLabels.map((tab) => (
                <TabsTrigger
                  key={tab.key}
                  value={tab.key}
                  className="min-w-[180px]"
                >
                  {tab.label}
                </TabsTrigger>
              ))}
            </TabsList>
            {tabLabels.map((tab) => (
              <TabsContent key={tab.key} value={tab.key} className="w-full">
                {(faqItems[tab.key]?.length ?? 0) > 0 ? (
                  <div className="grid grid-cols-1 gap-8">
                    {/* Left column: even-index FAQ items */}
                    <Accordion type="multiple" className="flex flex-col gap-4">
                      {(faqItems[tab.key] ?? []).map(
                        (item: FAQItem, index: number) => (
                          <AccordionItem
                            key={`item-${index}`}
                            value={`item-${index * 2}`}
                            className="border-b border-gray-200"
                          >
                            <AccordionTrigger>{item.question}</AccordionTrigger>
                            <AccordionContent>
                              <p>{item.answer}</p>
                            </AccordionContent>
                          </AccordionItem>
                        )
                      )}
                    </Accordion>
                  </div>
                ) : (
                  <div className="text-center text-muted-foreground py-12">
                    No FAQs available for this section.
                  </div>
                )}
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </Container>
    </section>
  );
}
